import React from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { CONTENT_MAX_WIDTH_STYLE } from "@/src/constants/size";

import CastingCarousel from "./CastingCarousel";
import CastingTranslations from "./CastingTranslations";
import { CASTING_TYPE } from "../../api/MediaAPI.types";
import "../../css/admin/utils/item-status.scss";
import { getTranslatedAttr } from "../../utils/data/Content";
import { smartlistEditPath } from "../../utils/smartlist";
import { CASTINGS_ROUTE, CONTENTS_ROUTE } from "../routes/routes";
import ContainerHeader from "../utils/ContainerHeader";
import FormNavigationHeader from "../utils/FormNavigationHeader";
import InfoTooltip from "../utils/InfoTooltip";
import { SlugProposition } from "../utils/SlugProposition";
import VariableList from "../variable_list/VariableList";

import type { JSX, RefObject } from "react";

import type { CastingTranslationsHandle } from "./CastingTranslations";
import type {
  CastingType,
  GetCastingResponse,
  MediaCasting,
} from "../../api/MediaAPI.types";
import type { AppState } from "../../reducers";

const navigationHeaderLinks = {
  en: "https://support.okast.tv/onUpdate/support/solutions/articles/103000097695-casting-creation-management",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097695-cr%C3%A9ation-et-gestion-des-castings",
};

interface CastingFormProps {
  onSubmit: () => void;
  casting: GetCastingResponse;
  setCasting: React.Dispatch<React.SetStateAction<GetCastingResponse>>;
  type: string;
  isLoading: boolean;
  translationsRef: RefObject<CastingTranslationsHandle | null>;
}

type MediaCastingByCastingType = Record<CastingType, MediaCasting[]>;

interface PreviewLink {
  link: string;
  projectName: string;
}

export default function CastingForm({
  onSubmit,
  casting,
  setCasting,
  type,
  isLoading,
  translationsRef,
}: CastingFormProps): JSX.Element {
  const { t } = useTranslation("editor-casting");
  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );

  const getMediaListByCastingType = (
    updatedCasting: GetCastingResponse,
  ): MediaCastingByCastingType => {
    const mediaListByCastingType: MediaCastingByCastingType = {
      actor: [],
      director: [],
      producer: [],
      author: [],
      sale: [],
      contact: [],
    };

    if (updatedCasting?.medias_castings) {
      updatedCasting.medias_castings.forEach((media) => {
        mediaListByCastingType[media.type]?.push(media);
      });
    }

    return mediaListByCastingType;
  };

  const handleChangeSlug = (slug: string): void => {
    setCasting({
      ...casting,
      slug,
    });
  };

  const handleVariablesChange = (variables: Record<string, string>): void => {
    setCasting({
      ...casting,
      custom_params: variables,
    });
  };

  const contentIdListByCastingType = getMediaListByCastingType(casting);
  const smartlistCastings = casting.smartlists_castings ?? [];

  const breadcrumbs = [
    {
      name: t("sectionTitle"),
      url: CASTINGS_ROUTE,
    },
    {
      name: t(type),
    },
  ];

  const computePreviewLink = (): string | PreviewLink[] => {
    if (projects.data.length === 1) {
      const link = `${projects.data[0]?.base_url}/casting/${
        casting?.slug || casting.uuid
      }`;
      return link;
    }
    return projects.data.reduce<PreviewLink[]>((acc, project) => {
      acc.push({
        link: `${project.base_url}/casting/${casting?.slug || casting.uuid}`,
        projectName: project.name,
      });
      return acc;
    }, []);
  };

  const castingTypeList = Object.values(CASTING_TYPE);

  const castingLabel = getTranslatedAttr(casting, "name") || t("newCasting");

  return (
    <div
      className="editor-form-casting-container editor-has-breadcrumbs mx-auto max-w-(--maxWidth)"
      style={CONTENT_MAX_WIDTH_STYLE}
    >
      <FormNavigationHeader
        breadcrumbs={breadcrumbs}
        links={navigationHeaderLinks}
      />
      <ContainerHeader
        withBackBtn
        defaultLabel={t("newCasting")}
        loading={isLoading}
        onSave={onSubmit}
        previewLink={computePreviewLink()}
        item={{
          casting,
          label: castingLabel,
        }}
      />
      <div className="editor-tile-container">
        <div className="editor-form-section section-separator">
          <div className="editor-form-section-title slug">
            {t("slug", { ns: "common" })}
            <InfoTooltip position="right" width="500px">
              {t("slug", { ns: "tooltip" })}
            </InfoTooltip>
          </div>
          <div className="editor-form-input-box-h">
            <SlugProposition
              disabled={false}
              id="casting"
              label={castingLabel}
              slug={casting?.slug}
              slugPropositionChange={handleChangeSlug}
              placeholder={`https://${t("yourProjectUrl", {
                ns: "editor-utils",
              })}/casting/`}
            />
          </div>
        </div>
        <CastingTranslations ref={translationsRef} casting={casting} />
        <div className="editor-form-section mt-[0.78125rem]">
          {castingTypeList.map((castingType) =>
            contentIdListByCastingType[castingType] &&
            contentIdListByCastingType[castingType].length > 0 ? (
              <div
                key={`carousel-section-${castingType}`}
                className="editor-form-section m-b-0 flex flex-col gap-2"
              >
                <div className="editor-form-section-title">
                  {t(castingType)}
                  <span className="editor-list-count">
                    {` (${contentIdListByCastingType[castingType].length})`}
                  </span>
                </div>
                <CastingCarousel
                  items={contentIdListByCastingType[castingType].map(
                    (media) => ({
                      id: media.uuid,
                      imageUrl: media.media_picture?.cover_picture_16_9?.url,
                      to: `${CONTENTS_ROUTE}/${media.media_type}/${media.media_uuid}`,
                    }),
                  )}
                />
              </div>
            ) : null,
          )}
        </div>
        {smartlistCastings.length > 0 ? (
          <div className="editor-form-section m-b-0 flex flex-col gap-2">
            <div className="editor-form-section-title">
              {t("curators")}
              <span className="editor-list-count">
                {` (${smartlistCastings.length})`}
              </span>
            </div>
            <CastingCarousel
              items={smartlistCastings.map((smartlist) => ({
                id: smartlist.uuid,
                imageUrl: smartlist.picture,
                label: smartlist.label,
                to: smartlistEditPath(
                  smartlist.storefront_project_name,
                  smartlist.type,
                  smartlist.uuid,
                ),
              }))}
            />
          </div>
        ) : null}
        <div className="editor-form-section">
          <div className="editor-form-section-title">
            {t("variables", { ns: "editor-utils" })}
          </div>
          <div className="editor-form-input">
            <VariableList
              onChange={handleVariablesChange}
              variables={casting?.custom_params}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
